import React, { useEffect, useState, useCallback } from "react";
import { notification, QRCode } from "antd";
import { DollarOutlined, QrcodeOutlined, SwapOutlined } from "@ant-design/icons";
import { getUserInfo, handleError, getToken } from "../../hooks/useHttp";
import { useTableSSE } from "../../hooks/useTableSSE";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import {
  BoardHeader,
  BottomBar,
  ConnectionStatus,
  PixelButton,
  PixelConfirm,
  PixelModal,
  PlayerScoreRow,
  ScreenFrame,
} from "../../design/retro";

// Frontend builds (including Cloudflare Pages) bake REACT_APP_* values at build time.
export const baseUrl = (
  process.env.REACT_APP_API_BASE_URL || "https://bip-be.ermis.network/api"
).replace(/\/$/, "");

export const revert = (data) => {
  data.sort(() => -1);
};

const TABLE_LOG = "__table_log__";

const timeConvert = (timestamp) => {
  return dayjs(new Date(timestamp)).format("HH:mm:ss, DD/MM");
};

function DashBoard() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState();
  const [tableId, setTableId] = useState();
  const [amount, setAmount] = useState("");
  const currentUser = getUserInfo();
  const [modal1Open, setModal1Open] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [modalData, setModalData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logModalTitle, setLogModalTitle] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [tableName, setTableName] = useState("");
  const { id } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { formatMessage } = useIntl();
  const t = (msgId, values) => formatMessage({ id: msgId }, values);

  const token = getToken();
  const pageUrl = window.location.href;

  // Use SSE hook for real-time updates
  const { tableData: sseTableData, isConnected } = useTableSSE(id, {
    enabled: !!id && isLoggedIn,
  });

  // Update tableData when SSE receives new data
  useEffect(() => {
    if (sseTableData && sseTableData.length > 0) {
      // Sort to keep stable order: Chicken first, then by joinedAt timestamp
      const sortedData = [...sseTableData].sort((a, b) => {
        if (a.username === "Chicken") return -1;
        if (b.username === "Chicken") return 1;
        // Sort by joinedAt to maintain stable order (API returns camelCase)
        const aTime = new Date(a.joinedAt || a.joined_at || 0).getTime();
        const bTime = new Date(b.joinedAt || b.joined_at || 0).getTime();
        return aTime - bTime;
      });
      setTableData(sortedData);
    }
  }, [sseTableData]);
 

  useEffect(() => {
    setIsLoggedIn(currentUser ? true : false);

    if (!currentUser) {
      if (id) {
        localStorage.setItem("pendingTableId", id);
      }
      navigate("/");
    }

    if (!token && currentUser) {
      notification.info({
        message: "Please log in to access this page",
        placement: "top",
      });
      navigate("/");
    }
  }, [currentUser, token, navigate, id]);

  const fetchTableData = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${baseUrl}/tables/${id}`, {
        method: "GET",
        headers,
      });

      const data = await res.json();

      if (res.ok && data) {
        setTableId(data.id);
        setTableName(data.tableName || data.name || "");
        const players = data.players;

        const convertedData = players.map((item) => {
          return { ...item, key: item.id };
        });

        convertedData.sort((a, b) => {
          if (a.username === "Chicken") return -1;
          if (b.username === "Chicken") return 1;
          // Sort by joinedAt to maintain stable order
          const aTime = new Date(a.joinedAt || a.joined_at || 0).getTime();
          const bTime = new Date(b.joinedAt || b.joined_at || 0).getTime();
          return aTime - bTime;
        });
        setTableData(convertedData);

      } else {
        notification.error({
          message: data.error || "Failed to fetch table data",
          placement: "top",
        });
      }
    } catch (e) {
      handleError(e);
    }
  }, [id, token]);

  // Function to fetch all transfer history from API
  const fetchTableHistory = useCallback(async () => {
    if (!tableId) return [];

    try {
      // fetch the table data
      await fetchTableData();
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(`${baseUrl}/tables/${tableId}/history`, {
        method: "GET",
        headers,
      });

      const data = await res.json();

      if (res.ok) {
        return data; // Return the transfers array
      } else {
        notification.error({
          message: data.error || "Failed to fetch transfer history",
          placement: "top",
        });
        return [];
      }
    } catch (e) {
      handleError(e);
      return [];
    }
  }, [tableId, token, fetchTableData]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData, id]);

  // Numpad handler: whole numbers only, max 6 digits.
  const handleNumpadPress = (value) => {
    if (value === "delete") {
      setAmount((prev) => prev.slice(0, -1));
    } else if (value === "clear") {
      setAmount("");
    } else {
      setAmount((prev) => {
        const next = prev === "0" ? value : prev + value;
        return next.length > 6 ? prev : next;
      });
    }
  };

  const getPlayerLogData = async (record, type) => {
    setIsLoading(true);
    try {
      const history = await fetchTableHistory();

      if (history.length === 0) {
        setLogData([]);
        setIsLoading(false);
        return;
      }

      let filteredHistory = [];
      const playerId = record.id;

      if (type === "all") {
        // For all: get both incoming and outgoing
        filteredHistory = history.filter((item) => 
          item.fromPlayerId === playerId || item.toPlayerId === playerId
        );
      } else if (type === "out") {
        filteredHistory = history.filter((item) => item.fromPlayerId === playerId);
      } else if (type === "in") {
        filteredHistory = history.filter((item) => item.toPlayerId === playerId);
      }

      // Mark each transaction as incoming or outgoing for this player
      const processedHistory = filteredHistory.map(item => ({
        ...item,
        isIncoming: item.toPlayerId === playerId,
        displayType: item.toPlayerId === playerId ? 'in' : 'out'
      }));

      // Reverse for newest first
      setLogData(processedHistory.reverse());
    } catch (error) {
      handleError(error);
      setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get all transfer history logs
  const getAllTransferHistory = async () => {
    setIsLoading(true);
    try {
      const history = await fetchTableHistory();

      if (history.length === 0) {
        setLogData([]);
        setIsLoading(false);
        return;
      }

      // Process all history
      const processedHistory = history.map(item => ({
        ...item,
        isIncoming: false,
        displayType: 'neutral'
      }));

      // Reverse for newest first
      setLogData(processedHistory.reverse());
    } catch (error) {
      handleError(error);
      setLogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter log data based on active tab and search
  const getFilteredLogData = () => {
    let filtered = logData;
    
    if (activeTab === "in") {
      filtered = logData.filter(item => item.displayType === 'in');
    } else if (activeTab === "out") {
      filtered = logData.filter(item => item.displayType === 'out');
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        (item.fromPlayerUsername?.toLowerCase().includes(query)) ||
        (item.toPlayerUsername?.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  // Handle transfer
  const handleTransfer = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      notification.error({
        message: t("board.invalidAmount"),
        placement: "top",
      });
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(
        `${baseUrl}/tables/${tableId}/players/${currentUser.id}/transfer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(amount),
            toPlayerId: modalData?.id,
          }),
        }
      );

      const data = await res.json();

      if (res.status === 400) {
        setIsLoading(false);
        const errMess = data.error || data.message;
        notification.error({
          message: errMess,
          placement: "top",
        });
      } else if (res.status === 200) {
        const message = `${formatMessage({
          id: "message.transferred",
        })} ${modalData?.username} ${amount}`;

        notification.success({
          message: message,
          placement: "top",
        });
        setModal1Open(false);
        setAmount("");
        fetchTableData();
        setIsLoading(false);
      } else {
        notification.error({
          message: data.error || "Transfer failed",
          placement: "top",
        });
        setIsLoading(false);
      }
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  const openPlayerLog = (player) => {
    setLogModalOpen(true);
    setLogModalTitle(t("board.historyOf", { name: player.username }));
    setActiveTab("all");
    getPlayerLogData(player, "all");
  };

  const openTableLog = () => {
    setLogModalOpen(true);
    setLogModalTitle(TABLE_LOG);
    setActiveTab("all");
    getAllTransferHistory();
  };

  const copyStreamLink = async () => {
    const viewerUrl = `${window.location.origin}/stream/view/${id}`;
    try {
      await navigator.clipboard.writeText(viewerUrl);
      notification.success({ message: t("board.streamCopied"), description: viewerUrl, placement: "top" });
    } catch (e) {
      notification.info({ message: viewerUrl, placement: "top" });
    }
  };

  const closeTransfer = () => {
    setAmount("");
    setModal1Open(false);
  };

  const renderPlayerCard = (player) => {
    const isCurrentUser = currentUser?.id === player.id;
    return (
      <PlayerScoreRow
        key={player.id}
        name={player.username}
        score={player.points}
        isCurrentUser={isCurrentUser}
        youLabel={t("board.you")}
        payIcon={<DollarOutlined />}
        logIcon={<SwapOutlined />}
        payLabel={t("board.payLabel", { name: player.username })}
        logLabel={t("board.logLabel", { name: player.username })}
        onPay={() => {
          setModalData(player);
          setAmount("");
          setModal1Open(true);
        }}
        onLog={() => openPlayerLog(player)}
      />
    );
  };

  // One log entry. For a player's own log, the amount is coloured by direction.
  const renderLogCard = (item, index) => {
    const type = item.displayType || "neutral";
    const isPlayerLog = type !== "neutral";
    const balanceBefore = item.isIncoming ? item.toBalanceBefore : item.fromBalanceBefore;
    const balanceAfter = item.isIncoming ? item.toBalanceAfter : item.fromBalanceAfter;
    const hasBalance = balanceBefore !== undefined && balanceBefore !== null && balanceAfter !== undefined && balanceAfter !== null;
    const amountClass = type === "in" ? "rt-amount-in" : type === "out" ? "rt-amount-out" : "";

    return (
      <div key={`${item.timestamp}-${index}`} className="rt-card">
        <div className="rt-card-row">
          <span className="rt-card-transfer">
            <span>{item.fromPlayerUsername}</span>
            <span className="rt-card-arrow" aria-hidden="true">→</span>
            <span>{item.toPlayerUsername}</span>
          </span>
          <span className={`rt-card-amount ${amountClass}`}>
            {type === "in" ? "+" : type === "out" ? "−" : ""}
            {item.amount}
          </span>
        </div>
        <div className="rt-card-row">
          <span className="rt-card-meta">{item.timestamp ? timeConvert(item.timestamp) : ""}</span>
          {isPlayerLog && hasBalance && (
            <span className="rt-card-meta">
              {balanceBefore} → {balanceAfter}
            </span>
          )}
        </div>
      </div>
    );
  };

  const isTableLogModal = logModalTitle === TABLE_LOG;
  const filteredLogData = [...getFilteredLogData()].sort(
    (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
  );

  const myPoints = tableData?.find((p) => p.id === currentUser?.id)?.points;
  const amountNumber = Number(amount) || 0;
  const hasMyPoints = myPoints !== undefined && myPoints !== null;

  // Redirect is in progress (not logged in): render nothing instead of a stale form.
  if (!isLoggedIn) return null;

  return (
    <>
      <ScreenFrame className="rt-board-screen">
        <BoardHeader
          left={
            <PixelButton variant="danger" size="sm" onClick={() => setLeaveConfirmOpen(true)}>
              {t("board.leave")}
            </PixelButton>
          }
          title={tableName || (id ? `#${String(id).slice(0, 8)}` : "")}
          status={
            <ConnectionStatus connected={isConnected} liveLabel={t("board.live")} offlineLabel={t("board.offline")} />
          }
          right={
            <PixelButton
              variant="paper"
              size="sm"
              className="rt-button-icon"
              onClick={() => setQrModalOpen(true)}
              aria-label={t("board.tableQr")}
            >
              <QrcodeOutlined />
            </PixelButton>
          }
        />

        <section className="rt-scoreboard-list" aria-label="Scoreboard">
          {tableData?.length ? (
            tableData.map((player) => renderPlayerCard(player))
          ) : (
            <p className="rt-empty">{t("ui.loading")}</p>
          )}
        </section>

        <BottomBar>
          <PixelButton variant="blue" onClick={openTableLog}>
            {t("board.tableLog")}
          </PixelButton>
          <PixelButton variant="paper" onClick={copyStreamLink}>
            {t("board.copyStream")}
          </PixelButton>
        </BottomBar>
      </ScreenFrame>

      {/* ---------------- Transfer (numpad) ---------------- */}
      <PixelModal
        open={modal1Open}
        onClose={closeTransfer}
        title={t("board.transferTo", { name: modalData?.username || "" })}
        size="sm"
        closeLabel={t("ui.close")}
      >
        <div className="rt-numpad">
          <div className="rt-numpad-display" aria-live="polite" aria-label={t("board.enterAmount")}>
            {amount || "0"}
          </div>
          {hasMyPoints && (
            <p className="rt-numpad-preview">
              {t("board.myPoints")}: <span className="rt-number">{myPoints}</span>
              {amountNumber > 0 && (
                <>
                  {" → "}
                  <span className="rt-number">{Number(myPoints) - amountNumber}</span>
                </>
              )}
            </p>
          )}
          <div className="rt-numpad-chips">
            {["10", "20", "50", "100"].map((chip) => (
              <PixelButton
                key={chip}
                size="sm"
                variant="paper"
                className={amount === chip ? "rt-numpad-chip-active" : ""}
                onClick={() => setAmount(chip)}
              >
                {chip}
              </PixelButton>
            ))}
          </div>
          <div className="rt-numpad-grid">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((num) => (
              <PixelButton key={num} variant="paper" onClick={() => handleNumpadPress(num)}>
                {num}
              </PixelButton>
            ))}
            <PixelButton variant="steel" onClick={() => handleNumpadPress("clear")}>
              <span className="rt-numpad-key-text">{t("board.clear")}</span>
            </PixelButton>
            <PixelButton variant="paper" onClick={() => handleNumpadPress("0")}>
              0
            </PixelButton>
            <PixelButton variant="steel" onClick={() => handleNumpadPress("delete")} aria-label={t("board.backspace")}>
              ⌫
            </PixelButton>
          </div>
          <PixelButton
            variant="primary"
            size="lg"
            block
            onClick={handleTransfer}
            disabled={!amountNumber || isLoading}
          >
            {isLoading
              ? t("board.processing")
              : amountNumber
                ? t("board.confirmTransfer", { amount })
                : t("board.enterAmount")}
          </PixelButton>
        </div>
      </PixelModal>

      {/* ---------------- Logs ---------------- */}
      <PixelModal
        title={isTableLogModal ? t("board.tableLog") : logModalTitle}
        open={logModalOpen}
        onClose={() => {
          setLogModalOpen(false);
          setActiveTab("all");
          setSearchQuery("");
        }}
        size="full"
        closeLabel={t("ui.close")}
      >
        {isLoading ? (
          <p className="rt-empty">{t("ui.loading")}</p>
        ) : filteredLogData.length > 0 ? (
          <div className="rt-card-list">{filteredLogData.map(renderLogCard)}</div>
        ) : (
          <p className="rt-empty">{t("board.noTransactions")}</p>
        )}
        {!isTableLogModal && (
          <PixelButton variant="blue" block onClick={openTableLog} className="rt-log-all-button">
            {t("board.showAllLog")}
          </PixelButton>
        )}
      </PixelModal>

      {/* ---------------- Table QR ---------------- */}
      <PixelModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title={t("board.tableQr")}
        closeLabel={t("ui.close")}
      >
        <div className="rt-qr-display">
          <div className="rt-qr-frame">
            <QRCode value={pageUrl} size={220} bordered={false} />
          </div>
          {tableName && <p className="rt-text">{tableName}</p>}
          <p className="rt-qr-id">ID: {tableId || id}</p>
        </div>
      </PixelModal>

      <PixelConfirm
        open={leaveConfirmOpen}
        title={t("board.leave")}
        message={t("board.leaveConfirm")}
        confirmText={t("board.leave")}
        cancelText={t("button.cancel")}
        onCancel={() => setLeaveConfirmOpen(false)}
        onConfirm={() => {
          setLeaveConfirmOpen(false);
          navigate("/");
        }}
      />
    </>
  );
}

export default DashBoard;
